import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BudgetAllocationModal = ({ budgetItem, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    department: '',
    category: '',
    budgetCode: '',
    allocated: '',
    description: '',
    approver: '',
    startDate: '',
    endDate: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (budgetItem) {
      setFormData({
        department: budgetItem?.department || '',
        category: budgetItem?.category || '',
        budgetCode: budgetItem?.budgetCode || '',
        allocated: budgetItem?.allocated?.toString() || '',
        description: budgetItem?.description || '',
        approver: budgetItem?.approver || '',
        startDate: budgetItem?.startDate || '',
        endDate: budgetItem?.endDate || '',
        notes: budgetItem?.notes || ''
      });
    } else {
      // Reset form for new budget item
      setFormData({
        department: '',
        category: '',
        budgetCode: '',
        allocated: '',
        description: '',
        approver: '',
        startDate: '',
        endDate: '',
        notes: ''
      });
    }
    setErrors({});
  }, [budgetItem, isOpen]);

  const departmentOptions = [
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'it', label: 'IT' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance' },
    { value: 'operations', label: 'Operations' },
    { value: 'rd', label: 'R&D' }
  ];

  const categoryOptions = [
    { value: 'marketing_campaigns', label: 'Marketing Campaigns' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'training_development', label: 'Training & Development' },
    { value: 'equipment_supplies', label: 'Equipment & Supplies' },
    { value: 'professional_services', label: 'Professional Services' },
    { value: 'research_projects', label: 'Research Projects' },
    { value: 'travel_expenses', label: 'Travel & Expenses' },
    { value: 'office_supplies', label: 'Office Supplies' },
    { value: 'software_licenses', label: 'Software Licenses' }
  ];

  const approverOptions = [
    { value: 'mamadou_diallo', label: 'Mamadou Diallo - CFO' },
    { value: 'fatoumata_toure', label: 'Fatoumata Touré - CTO' },
    { value: 'ibrahima_camara', label: 'Ibrahima Camara - CHRO' },
    { value: 'hawa_bangoura', label: 'Hawa Bangoura - COO' },
    { value: 'alseny_sylla', label: 'Alsény Sylla - Finance Director' },
    { value: 'aminata_diakite', label: 'Dr. Aminata Diakité - R&D Director' }
  ];

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.department) newErrors.department = 'Department is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    if (!formData?.budgetCode) newErrors.budgetCode = 'Budget code is required';
    if (!formData?.allocated) newErrors.allocated = 'Budget amount is required';
    if (!formData?.approver) newErrors.approver = 'Approver is required';
    if (!formData?.startDate) newErrors.startDate = 'Start date is required';
    if (!formData?.endDate) newErrors.endDate = 'End date is required';

    // Validate budget amount is a positive number
    if (formData?.allocated && (isNaN(formData?.allocated) || parseFloat(formData?.allocated) <= 0)) {
      newErrors.allocated = 'Budget amount must be a positive number';
    }

    // Validate end date is after start date
    if (formData?.startDate && formData?.endDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({
        ...formData,
        allocated: parseFloat(formData?.allocated),
        id: budgetItem?.id || Date.now() // Generate new ID for new items
      });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto card-shadow">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="PieChart" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                {budgetItem ? 'Edit Budget Allocation' : 'Create Budget Allocation'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {budgetItem ? 'Update budget allocation details' : 'Set up a new budget allocation'}
              </p>
            </div>
          </div>
          <Button variant="ghost" iconName="X" onClick={onClose} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Department *
                </label>
                <Select
                  options={departmentOptions}
                  value={formData?.department}
                  onChange={(value) => handleInputChange('department', value)}
                  placeholder="Select Department"
                  error={errors?.department}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Category *
                </label>
                <Select
                  options={categoryOptions}
                  value={formData?.category}
                  onChange={(value) => handleInputChange('category', value)}
                  placeholder="Select Category"
                  error={errors?.category}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Budget Code *
                </label>
                <Input
                  type="text"
                  value={formData?.budgetCode}
                  onChange={(e) => handleInputChange('budgetCode', e?.target?.value)}
                  placeholder="e.g., SALES-MKT-001"
                  error={errors?.budgetCode}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Budget Amount * {formData?.allocated && `(${formatCurrency(formData?.allocated)})`}
                </label>
                <Input
                  type="number"
                  value={formData?.allocated}
                  onChange={(e) => handleInputChange('allocated', e?.target?.value)}
                  placeholder="Enter amount"
                  error={errors?.allocated}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Description
              </label>
              <Input
                type="text"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder="Brief description of budget allocation"
              />
            </div>
          </div>

          {/* Timeline & Approval */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Timeline & Approval</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Start Date *
                </label>
                <Input
                  type="date"
                  value={formData?.startDate}
                  onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                  error={errors?.startDate}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  End Date *
                </label>
                <Input
                  type="date"
                  value={formData?.endDate}
                  onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                  error={errors?.endDate}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Approver *
              </label>
              <Select
                options={approverOptions}
                value={formData?.approver}
                onChange={(value) => handleInputChange('approver', value)}
                placeholder="Select Approver"
                error={errors?.approver}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Notes & Comments
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              placeholder="Add any additional notes or comments..."
              rows="3"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="default" iconName="Save">
              {budgetItem ? 'Update Budget' : 'Create Budget'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetAllocationModal;